import React, {useEffect, useState} from 'react';
import {Button, Paper} from '@mui/material';
import {useTranslation} from 'react-i18next';
import api from '../../../api/axios';

import Lottie from 'lottie-react';
import loadingAnimation from '../../../assets/lottie/loading.json';

import {toast} from 'react-toastify';
import {useSearchParams} from 'react-router-dom';

// Declaring the Crisp interface for the global window object
declare global {
  interface Window {
    $crisp: any[];
    openCrispChat?: () => void;
  }
}

type KycStatus =
    | 'NOT_VERIFIED'
    | 'NOT_STARTED'
    | 'PENDING'
    | 'REVIEW'
    | 'RENEW'
    | 'APPROVED'
    | 'DECLINED';

type RejectionReason = {
  errorType: string;
  shortDescription: string;
  longDescription: string;
};

type KycStatusResponse = {
  status: KycStatus;
  rejectionReason?: RejectionReason[];
  url?: string | null;
};

const Kyc = () => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState<KycStatus | null>(null);
  const [rejectionReason, setRejectionReason] = useState<RejectionReason[] | null>(null);
  const [verificationUrl, setVerificationUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [iframeVisible, setIframeVisible] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get<KycStatusResponse>('/profile/kyc/status');
      setStatus(response.data.status);
      setRejectionReason(response.data.rejectionReason ?? null);
      setVerificationUrl(response.data.url ?? null);

      const showIframe =
          (response.data.status === 'NOT_STARTED' || response.data.status === 'NOT_VERIFIED') &&
          !!response.data.url;

      setIframeVisible(showIframe);
      if (showIframe) setIframeLoading(true);
    } catch (error) {
      toast.error('Failed to load KYC status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    if (!iframeVisible || !verificationUrl) return;

    setIframeLoading(true);

    const timeout = setTimeout(() => {
      setIframeLoading(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [iframeVisible, verificationUrl]);

  useEffect(() => {
    const verificationSessionId = searchParams.get('verificationSessionId');
    const statusParam = searchParams.get('status');

    if (verificationSessionId && statusParam) {
      const optimisticStatus: KycStatus =
          statusParam.toUpperCase() === 'APPROVED' ? 'APPROVED' : 'DECLINED';

      setStatus(optimisticStatus);
      setIframeVisible(false);

      api
      .post('/profile/kyc/confirm', {
        verificationSessionId,
        status: statusParam
      })
      .then(res => {
        setStatus(res.data.status);
        setRejectionReason(res.data.rejectionReason ?? null);
      })
      .catch(err => {
        console.error('Failed to confirm KYC', err);
        toast.error('Failed to confirm KYC status');
      });
    }
  }, [searchParams]);

  const startKyc = async () => {
    setLoading(true);
    try {
      const response = await api.post<KycStatusResponse>('/profile/kyc/start');
      setStatus(response.data.status);
      setVerificationUrl(response.data.url ?? null);

      const showIframe =
          (response.data.status === 'NOT_STARTED' || response.data.status === 'NOT_VERIFIED') &&
          !!response.data.url;

      setIframeVisible(showIframe);
      if (showIframe) setIframeLoading(true);
    } catch (error) {
      console.error('Failed to start KYC', error);
      toast.error('Failed to start verification');
    } finally {
      setLoading(false);
    }
  };

  const renderStatusBadge = () => {
    if (!status) return <div className="h-4 w-32 bg-gray-700/40 rounded animate-pulse"/>;

    switch (status) {
      case 'APPROVED':
        return <span className="text-green-500 font-semibold">Verified</span>;
      case 'PENDING':
      case 'REVIEW':
        return <span className="text-yellow-500 font-semibold">In review</span>;
      case 'DECLINED':
        return <span className="text-red-500 font-semibold">Declined</span>;
      default:
        return <span className="text-gray-400 font-semibold">Not verified</span>;
    }
  };

  const openVerificationInNewTab = () => {
    if (verificationUrl) {
      window.open(verificationUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const openSupportChat = () => {
    if (window.openCrispChat) {
      // Use centralized function to ensure visibility
      window.openCrispChat();

      // Still push session data for context
      if (window.$crisp) {
        window.$crisp.push(['set', 'session:data', [[['kyc-status', status]]]]);
      }
    } else {
      toast.error('Chat is currently unavailable');
    }
  };

  return (
      <Paper className="bg-light dark:bg-dark shadow-md rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-400 font-semibold">
              Status: {renderStatusBadge()}
            </div>

            {!loading && (status === 'NOT_STARTED' || status === 'NOT_VERIFIED') && !verificationUrl && (
                <Button variant="contained" color="primary" onClick={startKyc} className="h-10">
                  Verify identity
                </Button>
            )}

            {!loading && (status === 'DECLINED' || status === 'REVIEW' || status === 'PENDING') && (
                <Button variant="contained" onClick={openSupportChat}>
                  Contact Support
                </Button>
            )}

            {loading && <div className="h-10 w-40 bg-gray-700/40 rounded animate-pulse"/>}
          </div>
        </div>

        {!(status === 'NOT_VERIFIED' && !iframeVisible) && (
            <div className="relative min-h-[320px] border-t border-gray-700/30">
              {iframeVisible && verificationUrl && (
                  <div className="relative hidden md:block">
                    {iframeLoading && (
                        <div className="absolute inset-0 flex justify-center items-center bg-white/70 dark:bg-black/70 z-10">
                          <Lottie animationData={loadingAnimation} className="h-72" loop/>
                        </div>
                    )}

                    <iframe
                        src={verificationUrl}
                        title="KYC Verification"
                        className={`min-h-[80vh] w-[700px] transition-opacity duration-300 ${
                            iframeLoading ? 'opacity-0 pointer-events-none' : 'opacity-100'
                        }`}
                        allow="camera; microphone; fullscreen; autoplay; encrypted-media"
                        onLoad={() => setIframeLoading(false)}
                    />
                  </div>
              )}

              {iframeVisible && verificationUrl && (
                  <div className="p-6 md:hidden">
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={openVerificationInNewTab}
                    >
                      Continue verification
                    </Button>
                  </div>
              )}

              {!iframeVisible && (
                  <div className="p-6 space-y-4">
                    {loading && (
                        <>
                          <div className="h-4 w-2/3 bg-gray-700/40 rounded animate-pulse"/>
                          <div className="h-20 w-full bg-gray-700/30 rounded animate-pulse"/>
                          <div className="h-20 w-full bg-gray-700/30 rounded animate-pulse"/>
                        </>
                    )}

                    {!loading && status === 'PENDING' && (
                        <div className="text-sm text-gray-400">
                          Your documents are currently under review.
                        </div>
                    )}

                    {!loading && status === 'APPROVED' && (
                        <div className="text-sm text-green-400">
                          Your identity has been successfully verified.
                        </div>
                    )}

                    {!loading && (status === 'DECLINED' || status === 'REVIEW') && rejectionReason && (
                        <>
                          <div className="text-sm font-semibold text-red-500">
                            Verification failed for the following reasons:
                          </div>

                          {rejectionReason.map((reason, index) => (
                              <div
                                  key={index}
                                  className="border border-red-500/20 rounded-md p-4 text-sm"
                              >
                                <div className="font-semibold text-red-400">{reason.shortDescription}</div>
                                <div className="mt-1 text-red-300">{reason.longDescription}</div>
                                <div className="mt-2 text-xs text-gray-400 flex flex-wrap gap-x-4">
                                  <span>Error type: {reason.errorType}</span>
                                </div>
                              </div>
                          ))}
                        </>
                    )}
                  </div>
              )}
            </div>
        )}
      </Paper>
  );
};

export default Kyc;